import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Award, CheckCircle2, XCircle, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Exam({ courseId, corso, capolavoro, onExit }) {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);

  const loadExam = () => {
    setLoading(true);
    setError(false);
    api.post(`/courses/${courseId}/exam/generate`)
      .then((res) => {
        const qs = res.data?.domande || [];
        if (!qs.length) { setError(true); return; }
        setQuestions(qs);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadExam(); /* eslint-disable-next-line */ }, [courseId]);

  const setAnswer = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));

  const submit = async () => {
    const unanswered = questions.filter((q) => !answers[q.id] && answers[q.id] !== "0");
    if (unanswered.length) {
      toast.error("Rispondi a tutte le domande prima di consegnare.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { answers: questions.map((q) => ({ question_id: q.id, answer: String(answers[q.id] ?? "") })) };
      const { data } = await api.post(`/courses/${courseId}/exam/submit`, payload);
      setResult(data);
      if (data.is_new_record && data.superato)
        toast.success(`${capolavoro ? "Capolavoro compiuto" : data.esito} · nuovo record ${data.percentuale}%!`);
      else if (data.superato)
        toast.success(`Record confermato: ${data.percentuale}%`);
      else toast.error("Prova non superata. Torna in Bottega e riprova.");
    } catch {
      toast.error("Errore durante la consegna della Prova");
    } finally {
      setSubmitting(false);
    }
  };

  const provaLabel = capolavoro ? "Il Capolavoro" : "Prova del Maestro";

  if (loading)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="py-32 flex flex-col items-center gap-4 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} />
          <p className="text-muted-foreground">Il Maestro sta preparando la Prova di {corso}...</p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10 text-center">
          <div className="bg-card border border-border rounded-md p-8" data-testid="exam-error">
            <XCircle className="h-12 w-12 mx-auto text-destructive/70 mb-4" strokeWidth={1.5} />
            <h1 className="font-serif text-2xl font-bold mb-2">Prova non disponibile</h1>
            <p className="text-muted-foreground mb-6">
              Non è stato possibile preparare la Prova in questo momento. Riprova tra poco.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={loadExam} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="exam-error-retry">
                Riprova
              </Button>
              <Button variant="outline" onClick={onExit} className="rounded-sm">
                Torna al corso
              </Button>
            </div>
          </div>
        </div>
      </div>
    );

  if (result)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
          <div className="bg-card border border-border rounded-md p-8 text-center">
            <Award className={`h-14 w-14 mx-auto mb-4 ${result.a_regola_arte ? "text-accent" : result.superato ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5} />
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-1">{provaLabel} · {corso}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1" data-testid="exam-record-label">
              {result.is_new_record ? "Nuovo record" : "Il tuo miglior risultato"}
            </p>
            <h1 className="font-serif text-5xl font-bold mb-2" data-testid="exam-voto">
              {result.percentuale}%
            </h1>
            <Badge className={`rounded-full mb-3 ${result.superato ? (result.a_regola_arte ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground") : "bg-destructive text-destructive-foreground"}`}>
              {result.esito}
            </Badge>
            {result.is_new_record && result.superato && (
              <p className="text-sm font-medium text-accent mb-6" data-testid="exam-new-record">
                Hai migliorato il tuo record!
              </p>
            )}
            {result.tentativo_corrente && !result.is_new_record && (
              <p className="text-sm text-muted-foreground mb-6" data-testid="exam-current-attempt">
                Tentativo di oggi: <strong className="text-foreground">{result.tentativo_corrente.percentuale}%</strong> · {result.tentativo_corrente.esito} <span className="opacity-70">(il record resta {result.percentuale}%)</span>
              </p>
            )}
            <p className="text-muted-foreground leading-relaxed mb-6">{result.giudizio}</p>
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground mb-8">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-primary" strokeWidth={1.5} />
                Risposte multiple: {result.mc_correct}/{result.mc_total}
              </span>
              {result.superato && <span>· {result.cfu} ⭐ stelle</span>}
              {result.grado_totale && <span>· Grado di maestria: <strong className="text-foreground">{result.grado_totale}</strong></span>}
            </div>
          </div>

          {result.feedback_aperte?.length > 0 && (
            <div className="mt-6 space-y-3">
              <h2 className="font-serif text-xl font-bold">Correzione risposte aperte</h2>
              {result.feedback_aperte.map((f, i) => (
                <div key={f.id} className="bg-card border border-border rounded-md p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">Domanda aperta {i + 1}</span>
                    <Badge variant="outline" className="rounded-full">{Math.round(f.punteggio * 100)}%</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.feedback}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3 mt-8">
            <Button onClick={onExit} className="flex-1 rounded-sm bg-primary hover:bg-primary/90" data-testid="exam-done-btn">
              Torna al corso
            </Button>
            <Button variant="outline" onClick={() => { setResult(null); setAnswers({}); window.scrollTo(0, 0); }}
              className="rounded-sm" data-testid="exam-retry-btn">
              Riprova
            </Button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-8">
        <button onClick={onExit} data-testid="exam-exit-btn"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Annulla
        </button>

        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-5 w-5 text-primary" strokeWidth={1.5} />
          <p className="text-sm uppercase tracking-widest text-muted-foreground">{provaLabel}</p>
        </div>
        <h1 className="font-serif text-3xl font-bold mb-2">{corso}</h1>
        <p className="text-muted-foreground text-sm mb-8">
          Rispondi a tutte le domande. Le risposte aperte saranno valutate dal Maestro. Il risultato ti assegna un grado di maestria.
        </p>

        <div className="space-y-6">
          {questions.map((q, i) => (
            <div key={q.id} className="bg-card border border-border rounded-md p-5" data-testid={`question-${q.id}`}>
              <div className="flex items-start gap-3 mb-4">
                <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center">{i + 1}</span>
                <p className="font-medium leading-snug pt-0.5">{q.testo}</p>
              </div>

              {q.tipo === "multipla" ? (
                <RadioGroup
                  value={answers[q.id] ?? ""}
                  onValueChange={(v) => setAnswer(q.id, v)}
                  className="space-y-2 ml-10"
                >
                  {q.opzioni.map((opt, oi) => (
                    <label key={`${q.id}-${oi}`} htmlFor={`${q.id}-${oi}`}
                      className="flex items-center gap-3 p-3 rounded-sm border border-border cursor-pointer hover:bg-secondary/50 transition-colors">
                      <RadioGroupItem value={String(oi)} id={`${q.id}-${oi}`} data-testid={`option-${q.id}-${oi}`} />
                      <span className="text-sm">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              ) : (
                <div className="ml-10">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Risposta aperta</Label>
                  <Textarea
                    value={answers[q.id] ?? ""}
                    onChange={(e) => setAnswer(q.id, e.target.value)}
                    placeholder="Argomenta la tua risposta..."
                    rows={5}
                    className="rounded-sm"
                    data-testid={`answer-${q.id}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <Button onClick={submit} disabled={submitting}
          className="w-full mt-8 rounded-sm bg-primary hover:bg-primary/90 h-12" data-testid="submit-exam-btn">
          {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {submitting ? "Il Maestro sta correggendo..." : "Consegna la Prova"}
        </Button>
      </div>
    </div>
  );
}
